import React, { useState, useEffect, useCallback } from 'react';
import {
  GitCommit, GitPullRequest, GitMerge,
  MessageSquare, AlertCircle, ExternalLink,
  Loader2, Inbox, Clock, Filter,
  ChevronDown, ChevronRight, Circle,
  ArrowRight, X, CheckCircle
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { cn } from '../../lib/utils';
import { deliveryDashboardApi } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';

// ─── Activity icon + color mapping ───────────────────────────────
const ACTIVITY_CONFIG = {
  commit:          { icon: GitCommit,      color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Commit' },
  commit_link:     { icon: GitCommit,      color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Commit' },
  pr_opened:       { icon: GitPullRequest, color: 'text-blue-500',    bg: 'bg-blue-500/10',    label: 'PR Opened' },
  pr_merged:       { icon: GitMerge,       color: 'text-purple-500',  bg: 'bg-purple-500/10',  label: 'PR Merged' },
  pr_closed:       { icon: GitPullRequest, color: 'text-gray-400',    bg: 'bg-gray-400/10',    label: 'PR Closed' },
  ticket_created:  { icon: AlertCircle,    color: 'text-green-500',   bg: 'bg-green-500/10',   label: 'Ticket Created' },
  ticket_closed:   { icon: CheckCircle,    color: 'text-red-400',     bg: 'bg-red-400/10',     label: 'Ticket Closed' },
  ticket_reopened: { icon: AlertCircle,    color: 'text-yellow-500',  bg: 'bg-yellow-500/10',  label: 'Reopened' },
  ticket_comment:  { icon: MessageSquare,  color: 'text-sky-500',     bg: 'bg-sky-500/10',     label: 'Comment' },
  // Legacy types (backwards compat)
  issue_created:   { icon: AlertCircle,    color: 'text-green-500',   bg: 'bg-green-500/10',   label: 'Ticket Created' },
  issue_closed:    { icon: CheckCircle,    color: 'text-red-400',     bg: 'bg-red-400/10',     label: 'Ticket Closed' },
  issue_reopened:  { icon: AlertCircle,    color: 'text-yellow-500',  bg: 'bg-yellow-500/10',  label: 'Reopened' },
  issue_comment:   { icon: MessageSquare,  color: 'text-sky-500',     bg: 'bg-sky-500/10',     label: 'Comment' },
};

const FILTER_TYPES = [
  { value: '', label: 'All Events' },
  { value: 'commit', label: 'Commits' },
  { value: 'pr_opened', label: 'PRs Opened' },
  { value: 'pr_merged', label: 'PRs Merged' },
  { value: 'ticket_comment', label: 'Comments' },
  { value: 'ticket_closed', label: 'Tickets Closed' },
];

// ─── Helpers ─────────────────────────────────────────────────────
const timeAgo = (d) => {
  if (!d) return '';
  try { return formatDistanceToNow(new Date(d), { addSuffix: true }); }
  catch { return ''; }
};

const prAgeBadge = (hours) => {
  if (hours == null) return null;
  if (hours < 72) return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-300 text-emerald-600 bg-emerald-50">{Math.round(hours)}h</Badge>;
  if (hours < 168) return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-600 bg-amber-50">{Math.round(hours / 24)}d</Badge>;
  return <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-300 text-red-600 bg-red-50">{Math.round(hours / 24)}d</Badge>;
};

const providerLabel = (provider) => {
  const map = { github: 'GitHub', github_issues: 'GitHub', gitlab: 'GitLab', azure_devops: 'Azure DevOps', jira: 'Jira', linear: 'Linear' };
  return map[provider] || provider;
};

// ─── Pipeline Column ─────────────────────────────────────────────
const PipelineColumn = ({ title, count, icon: Icon, color, items, onFeatureClick }) => (
  <div className="flex-1 min-w-[180px]" data-testid={`pipeline-col-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={cn("flex items-center gap-2 mb-3 pb-2 border-b-2", color)}>
      <Icon className="w-4 h-4" />
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">{title}</span>
      <span className="ml-auto text-xs font-bold text-gray-400">{count}</span>
    </div>
    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
      {items.length === 0 && (
        <p className="text-xs text-gray-400 italic py-4 text-center">None</p>
      )}
      {items.map((f) => (
        <button
          key={f.feature_id}
          onClick={() => onFeatureClick?.(f.feature_id)}
          className="w-full text-left p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-600 transition-colors group"
          data-testid={`pipeline-feature-${f.feature_id}`}
        >
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
            {f.title}
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {f.assignee && (
              <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[90px]">{f.assignee}</span>
            )}
            {f.ticket && (
              <a
                href={f.ticket.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[10px] text-blue-500 hover:underline"
              >
                {f.ticket.key}
              </a>
            )}
            {f.commit_count > 0 && (
              <span className="text-[10px] text-gray-400">{f.commit_count} commit{f.commit_count !== 1 ? 's' : ''}</span>
            )}
            {f.open_pr_count > 0 && (
              <span className="text-[10px] text-purple-500">{f.open_pr_count} open PR{f.open_pr_count !== 1 ? 's' : ''}</span>
            )}
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ─── Feature Link (handles single or multiple features) ──────────
const FeatureLinks = ({ activity, onFeatureClick }) => {
  const featureTitles = activity.feature_titles || {};
  const featureIds = activity.feature_ids || [];
  
  // If we have multiple features, show them all
  if (featureIds.length > 1) {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        <ArrowRight className="w-3 h-3 text-gray-300 shrink-0" />
        {featureIds.map((fid, i) => (
          <React.Fragment key={fid}>
            {i > 0 && <span className="text-[10px] text-gray-300">,</span>}
            <button
              onClick={() => onFeatureClick?.(fid)}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[150px]"
            >
              {featureTitles[fid] || activity.feature_title || fid}
            </button>
          </React.Fragment>
        ))}
      </span>
    );
  }
  
  // Single feature
  if (activity.feature_title) {
    return (
      <>
        <ArrowRight className="w-3 h-3 text-gray-300" />
        <button
          onClick={() => onFeatureClick?.(activity.feature_id)}
          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[200px]"
        >
          {activity.feature_title}
        </button>
      </>
    );
  }
  
  return null;
};

// ─── Activity Item ───────────────────────────────────────────────
const ActivityItem = ({ activity, onFeatureClick }) => {
  const config = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.commit;
  const Icon = config.icon;
  const meta = activity.metadata || {};

  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={`activity-${activity.activity_id}`}>
      <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5", config.bg)}>
        <Icon className={cn("w-3.5 h-3.5", config.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            {activity.display_name || activity.user_name || 'Unknown'}
          </span>
          <span className="text-xs text-gray-400">{config.label}</span>
          {activity.repo && (
            <span className="text-[10px] text-gray-400 font-mono">{activity.repo.split('/').pop()}</span>
          )}
          <FeatureLinks activity={activity} onFeatureClick={onFeatureClick} />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
          {activity.content}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-gray-400">{timeAgo(activity.created_at)}</span>
          {activity.provider && (
            <span className="text-[10px] text-gray-300">{providerLabel(activity.provider)}</span>
          )}
          {(meta.pr_url || meta.issue_url || meta.comment_url) && (
            <a href={meta.comment_url || meta.pr_url || meta.issue_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5">
              <ExternalLink className="w-2.5 h-2.5" /> View
            </a>
          )}
          {meta.commit_sha && (
            <span className="text-[10px] font-mono text-gray-400">{meta.commit_sha.slice(0, 7)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const DeliveryTab = ({ productId }) => {
  const [pipeline, setPipeline] = useState(null);
  const [openPRs, setOpenPRs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityFilter, setActivityFilter] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const [expanded, setExpanded] = useState({
    activity: true,
    openPRs: true
  });

  const loadData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [pipelineRes, prsRes, activityRes] = await Promise.all([
        deliveryDashboardApi.getPipeline(productId).catch(() => ({ data: null })),
        deliveryDashboardApi.getOpenPRs(productId).catch(() => ({ data: { open_prs: [] } })),
        deliveryDashboardApi.getActivity(productId, { limit: 30 }).catch(() => ({ data: { activities: [] } })),
      ]);
      setPipeline(pipelineRes.data);
      setOpenPRs(prsRes.data?.open_prs || []);
      setActivities(activityRes.data?.activities || []);
    } catch (err) {
      console.error('Failed to load delivery dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadFilteredActivity = useCallback(async (type) => {
    try {
      const res = await deliveryDashboardApi.getActivity(productId, {
        limit: 30,
        activity_type: type || undefined
      });
      setActivities(res.data?.activities || []);
    } catch (err) {
      console.error('Failed to filter activity:', err);
    }
  }, [productId]);

  const handleFilterChange = (type) => {
    setActivityFilter(type);
    setShowFilterDropdown(false);
    loadFilteredActivity(type);
  };

  const handleFeatureClick = (featureId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('feature', featureId);
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="delivery-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const pipelineData = pipeline?.pipeline || { no_activity: [], commits_only: [], pr_open: [], code_complete: [] };
  const totalFeatures = pipeline?.total || 0;

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6">
        <div className="max-w-6xl mx-auto space-y-6">

          <div className="mb-2" data-testid="delivery-header">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Delivery</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
              Source control &amp; ticketing activity across your features
            </p>
          </div>

          {/* ═══════════ Section 1: Feature Delivery Pipeline ═══════════ */}
          <Card className="border border-gray-200 dark:border-gray-700" data-testid="feature-pipeline">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Feature Delivery Pipeline</h3>
                <p className="text-xs text-gray-400 mt-0.5">{totalFeatures} in-flight feature{totalFeatures !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <CardContent className="pt-4 pb-4">
              {totalFeatures === 0 ? (
                <div className="text-center py-10" data-testid="pipeline-empty">
                  <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No features in active development</p>
                  <p className="text-xs text-gray-400 mt-1">Features will appear here when they are "In Progress" or part of an active sprint</p>
                </div>
              ) : (
                <div className="flex gap-4 overflow-x-auto" data-testid="pipeline-columns">
                  <PipelineColumn
                    title="No Activity"
                    count={pipelineData.no_activity.length}
                    icon={Circle}
                    color="border-gray-300"
                    items={pipelineData.no_activity}
                    onFeatureClick={handleFeatureClick}
                  />
                  <PipelineColumn
                    title="Commits"
                    count={pipelineData.commits_only.length}
                    icon={GitCommit}
                    color="border-emerald-400"
                    items={pipelineData.commits_only}
                    onFeatureClick={handleFeatureClick}
                  />
                  <PipelineColumn
                    title="PR Open"
                    count={pipelineData.pr_open.length}
                    icon={GitPullRequest}
                    color="border-blue-400"
                    items={pipelineData.pr_open}
                    onFeatureClick={handleFeatureClick}
                  />
                  <PipelineColumn
                    title="Code Complete"
                    count={pipelineData.code_complete.length}
                    icon={GitMerge}
                    color="border-purple-400"
                    items={pipelineData.code_complete}
                    onFeatureClick={handleFeatureClick}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* ═══════════ Section 2: Recent Dev Activity ═══════════ */}
          <Collapsible open={expanded.activity} onOpenChange={() => setExpanded(p => ({ ...p, activity: !p.activity }))}>
            <Card className="border border-gray-200 dark:border-gray-700" data-testid="dev-activity-feed">
              <CollapsibleTrigger asChild>
                <div className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                      <GitCommit className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Recent Dev Activity</h3>
                      <p className="text-xs text-gray-400">Commits, PRs, tickets &amp; comments across all features</p>
                    </div>
                  </div>
                  {expanded.activity
                    ? <ChevronDown className="w-4 h-4 text-gray-400" />
                    : <ChevronRight className="w-4 h-4 text-gray-400" />
                  }
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="border-t border-gray-100 dark:border-gray-700 pt-3 pb-4">
                  <div className="flex items-center gap-2 mb-3 relative" data-testid="activity-filter-bar">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 gap-1"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      data-testid="activity-filter-btn"
                    >
                      <Filter className="w-3 h-3" />
                      {FILTER_TYPES.find(f => f.value === activityFilter)?.label || 'All Events'}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                    {activityFilter && (
                      <Button variant="ghost" size="sm" className="text-xs h-7 px-1.5" onClick={() => handleFilterChange('')}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                    {showFilterDropdown && (
                      <div className="absolute top-8 left-0 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[160px]">
                        {FILTER_TYPES.map(ft => (
                          <button
                            key={ft.value}
                            className={cn(
                              "w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700",
                              activityFilter === ft.value && "bg-gray-100 dark:bg-gray-700 font-medium"
                            )}
                            onClick={() => handleFilterChange(ft.value)}
                          >
                            {ft.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {activities.length === 0 ? (
                    <div className="text-center py-8" data-testid="activity-empty">
                      <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No activity yet</p>
                      <p className="text-xs text-gray-400 mt-1">Activity from source control and ticketing will appear here</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50" data-testid="activity-list">
                      {activities.map(a => (
                        <ActivityItem key={a.activity_id} activity={a} onFeatureClick={handleFeatureClick} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* ═══════════ Section 3: Open Items ═══════════ */}
          <Collapsible open={expanded.openPRs} onOpenChange={() => setExpanded(p => ({ ...p, openPRs: !p.openPRs }))}>
            <Card className="border border-gray-200 dark:border-gray-700" data-testid="open-prs-section">
              <CollapsibleTrigger asChild>
                <div className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <GitPullRequest className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Open Pull Requests</h3>
                      <p className="text-xs text-gray-400">{openPRs.length} open PR{openPRs.length !== 1 ? 's' : ''} across repos</p>
                    </div>
                  </div>
                  {expanded.openPRs
                    ? <ChevronDown className="w-4 h-4 text-gray-400" />
                    : <ChevronRight className="w-4 h-4 text-gray-400" />
                  }
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="border-t border-gray-100 dark:border-gray-700 pt-3 pb-4">
                  {openPRs.length === 0 ? (
                    <div className="text-center py-8" data-testid="prs-empty">
                      <GitPullRequest className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No open pull requests</p>
                      <p className="text-xs text-gray-400 mt-1">PRs linked to features will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-0 divide-y divide-gray-100 dark:divide-gray-700/50" data-testid="prs-list">
                      {openPRs.map((pr, idx) => (
                        <div key={`${pr.pr_number}-${idx}`} className="flex items-center gap-3 py-2.5" data-testid={`pr-row-${pr.pr_number}`}>
                          <GitPullRequest className="w-4 h-4 text-blue-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <a
                                href={pr.pr_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 truncate"
                              >
                                #{pr.pr_number} {pr.pr_title}
                              </a>
                              {prAgeBadge(pr.age_hours)}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {/* Show all linked features */}
                              {(pr.features || []).length > 0 ? (
                                pr.features.map((f, fi) => (
                                  <button
                                    key={f.feature_id}
                                    onClick={() => handleFeatureClick(f.feature_id)}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[200px]"
                                  >
                                    {fi > 0 && ', '}{f.feature_title}
                                  </button>
                                ))
                              ) : pr.feature_title && (
                                <button
                                  onClick={() => handleFeatureClick(pr.feature_id)}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[200px]"
                                >
                                  {pr.feature_title}
                                </button>
                              )}
                              {pr.repo && <span className="text-[10px] text-gray-400">{pr.repo.split('/').pop()}</span>}
                              {pr.author && <span className="text-[10px] text-gray-400">by {pr.author}</span>}
                              <span className="text-[10px] text-gray-400">{timeAgo(pr.linked_at)}</span>
                            </div>
                          </div>
                          <a href={pr.pr_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                            <ExternalLink className="w-3.5 h-3.5 text-gray-400 hover:text-blue-500" />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

        </div>
      </div>
    </div>
  );
};

export default DeliveryTab;
